// src/data/trainers/BodyweightTrainers.js

// Sarah Chen's workout programs
const sarahChenPrograms = [
    {
        id: 'beginner-path',
        name: 'Beginner Path',
        description: 'Foundation building with strategic muscle group pairings for gradual progression',
        pattern: 'w@d^_d', // legs, chest, back, arms, shoulders, core
        reps: 8,
        sets: 2,
        focus: 'Foundation & Form'
    },
    {
        id: 'intermediate-path',
        name: 'Intermediate Path',
        description: 'Push/pull sequencing to maximize recovery while maintaining workout intensity',
        pattern: 'w^@d_d',
        reps: 12,
        sets: 3,
        focus: 'Split Routines'
    },
    {
        id: 'advanced-path',
        name: 'Advanced Path',
        description: 'Contrast sequencing alternating push/pull for maximum neural engagement',
        pattern: '~@dd',
        reps: 6,
        sets: 5,
        focus: 'Strength Building'
    },
    {
        id: 'hiit-path',
        name: 'HIIT Path',
        description: 'High-intensity intervals pairing non-competing muscle groups',
        pattern: 'w~d^_',
        reps: 10,
        sets: 4,
        focus: 'Cardiovascular Conditioning'
    },
    {
        id: 'endurance-path',
        name: 'Endurance Path',
        description: 'Wave-loading pattern for metabolic conditioning effects',
        pattern: '~w@d',
        reps: 15,
        sets: 3,
        focus: 'Endurance & Stamina'
    },
    {
        id: 'strength-path',
        name: 'Strength Path',
        description: 'Antagonistic supersets optimizing recovery while maintaining training density',
        pattern: 'w@^d',
        reps: 6,
        sets: 4,
        focus: 'Maximum Strength'
    }
];

// All bodyweight trainers data
export const bodyweightTrainers = {
    'sarah-chen': {
        id: 'sarah-chen',
        name: 'Sarah Chen',
        specialization: 'Body Weight Training Specialist',
        image: '/assets/images/v22.jpg',
        experience: '8 years',
        category: 'bodyweight',
        intro: `Sarah Chen is a former competitive gymnast who transformed her passion for body movement into a career as a body weight training specialist. With her athletic build, shoulder-length black hair often pulled into a practical ponytail, and energetic demeanor, Sarah specializes in helping people of all fitness levels build strength using just their body weight. Her philosophy centers on mastering basic movement patterns before progressing to more complex exercises. "Your body is the only gym you'll ever need," is her favorite saying. Sarah's approach emphasizes proper form, gradual progression, and consistency—making fitness accessible to everyone regardless of their starting point.`,
        programs: sarahChenPrograms
    },
    'marcus-steel': {
        id: 'marcus-steel',
        name: 'Marcus Steel',
        specialization: 'Functional Movement Expert',
        image: '/assets/images/v82.jpg',
        experience: '12 years',
        category: 'bodyweight',
        intro: `Marcus Steel brings military precision to bodyweight training. A former Special Forces operator, Marcus understands the importance of functional strength that translates to real-world applications. His rugged appearance, complete with tactical gear and a disciplined demeanor, reflects his no-nonsense approach to fitness. Marcus specializes in compound movements that build practical strength, endurance, and mental toughness. His training philosophy: "Train for life, not just the gym." Marcus's programs focus on movements that improve daily functionality while building impressive strength.`,
        programs: [
            {
                id: 'tactical-foundation',
                name: 'Tactical Foundation',
                description: 'Military-inspired basic movements for functional strength',
                pattern: '~w@^',
                reps: 10,
                sets: 3,
                focus: 'Functional Basics'
            },
            {
                id: 'combat-conditioning',
                name: 'Combat Conditioning',
                description: 'High-intensity combat movements for total conditioning',
                pattern: 'w@d^_!',
                reps: 8,
                sets: 4,
                focus: 'Combat Training'
            },
            {
                id: 'operator-strength',
                name: 'Operator Strength',
                description: 'Advanced strength protocols for elite performance',
                pattern: '@^w~d',
                reps: 6,
                sets: 5,
                focus: 'Elite Strength'
            },
            {
                id: 'field-ready',
                name: 'Field Ready',
                description: 'Practical movements for real-world application',
                pattern: '~@w^d!',
                reps: 12,
                sets: 3,
                focus: 'Field Application'
            },
            {
                id: 'endurance-warrior',
                name: 'Endurance Warrior',
                description: 'Military endurance protocols for stamina building',
                pattern: 'w@d~^',
                reps: 15,
                sets: 3,
                focus: 'Military Endurance'
            },
            {
                id: 'tactical-athlete',
                name: 'Tactical Athlete',
                description: 'Complete athlete development for tactical performance',
                pattern: '@w^d~!',
                reps: 10,
                sets: 4,
                focus: 'Tactical Performance'
            }
        ]
    },
    'phoenix-wright': {
        id: 'phoenix-wright',
        name: 'Phoenix Wright',
        specialization: 'Calisthenics Master',
        image: '/assets/images/v83.jpg',
        experience: '10 years',
        category: 'bodyweight',
        intro: `Phoenix Wright is a street workout legend who transformed from an average office worker into a calisthenics phenomenon. Known for his incredible muscle-ups, human flags, and gravity-defying moves, Phoenix embodies the artistic side of bodyweight training. His lean, muscular physique and urban style reflect his street workout roots. Phoenix specializes in skill-based movements and progressive calisthenics that build both strength and artistry. His motto: "Defy gravity, define yourself." Phoenix's programs focus on building the strength and technique needed for advanced calisthenics skills.`,
        programs: [
            {
                id: 'street-basics',
                name: 'Street Basics',
                description: 'Foundation skills for street workout mastery',
                pattern: 'w@^~',
                reps: 8,
                sets: 3,
                focus: 'Skill Foundation'
            },
            {
                id: 'park-warrior',
                name: 'Park Warrior',
                description: 'Intermediate calisthenics for park training',
                pattern: '@w^d_',
                reps: 10,
                sets: 4,
                focus: 'Park Training'
            },
            {
                id: 'gravity-defier',
                name: 'Gravity Defier',
                description: 'Advanced skills that seem to defy gravity',
                pattern: '^@wd~',
                reps: 6,
                sets: 5,
                focus: 'Advanced Skills'
            },
            {
                id: 'urban-flow',
                name: 'Urban Flow',
                description: 'Flowing movement combinations for street style',
                pattern: 'w@d^_~',
                reps: 12,
                sets: 3,
                focus: 'Flow & Style'
            },
            {
                id: 'freestyle-master',
                name: 'Freestyle Master',
                description: 'Creative combinations and freestyle elements',
                pattern: '@^w~d!',
                reps: 8,
                sets: 4,
                focus: 'Freestyle Skills'
            },
            {
                id: 'calisthenics-elite',
                name: 'Calisthenics Elite',
                description: 'Elite-level skills for competition preparation',
                pattern: '^@w~d_',
                reps: 5,
                sets: 6,
                focus: 'Competition Prep'
            }
        ]
    },
    'alex-steele': {
        id: 'alex-steele',
        name: 'Alex Steele',
        specialization: 'Bodyweight Conditioning Coach',
        image: '/assets/images/v88.jpg',
        experience: '9 years',
        category: 'bodyweight',
        intro: `Alex Steele is a former CrossFit Games competitor who discovered the power of bodyweight training during injury recovery. With an athletic build and infectious enthusiasm, Alex brings high-energy conditioning to bodyweight movements. Specializing in metabolic conditioning and endurance, Alex's programs are designed to push cardiovascular limits while building functional strength. Their philosophy: "Your heart is your engine, your body is your vehicle." Alex's training focuses on building the conditioning needed for peak performance in any sport or activity.`,
        programs: [
            {
                id: 'metabolic-foundation',
                name: 'Metabolic Foundation',
                description: 'Building cardiovascular base with bodyweight movements',
                pattern: '~w@d',
                reps: 12,
                sets: 3,
                focus: 'Cardio Base'
            },
            {
                id: 'conditioning-beast',
                name: 'Conditioning Beast',
                description: 'High-intensity conditioning for athletic performance',
                pattern: 'w@^d!_',
                reps: 15,
                sets: 4,
                focus: 'Athletic Conditioning'
            },
            {
                id: 'endurance-machine',
                name: 'Endurance Machine',
                description: 'Extended endurance protocols for stamina building',
                pattern: '@wd^~_',
                reps: 20,
                sets: 3,
                focus: 'Endurance Building'
            },
            {
                id: 'crossfit-bodyweight',
                name: 'CrossFit Bodyweight',
                description: 'CrossFit-style WODs using only bodyweight',
                pattern: 'w@d!^_',
                reps: 10,
                sets: 5,
                focus: 'CrossFit Style'
            },
            {
                id: 'cardio-crusher',
                name: 'Cardio Crusher',
                description: 'Maximum cardiovascular challenge with bodyweight',
                pattern: '!@wd^_',
                reps: 8,
                sets: 6,
                focus: 'Cardio Intensity'
            },
            {
                id: 'performance-peak',
                name: 'Performance Peak',
                description: 'Peak conditioning for competitive athletes',
                pattern: '@w!^d_',
                reps: 12,
                sets: 4,
                focus: 'Peak Performance'
            }
        ]
    },
    'jack-reynolds': {
        id: 'jack-reynolds',
        name: 'Jack Reynolds',
        specialization: 'Progressive Overload Expert',
        image: '/assets/images/v75.jpg',
        experience: '11 years',
        category: 'bodyweight',
        intro: `Jack Reynolds is a former powerlifter who mastered the art of progressive overload in bodyweight training. After reaching elite levels in traditional lifting, Jack discovered how to apply powerlifting principles to bodyweight movements. His muscular frame and methodical approach reflect his background in strength sports. Jack specializes in progression schemes that continuously challenge the body using leverage, range of motion, and tempo. His philosophy: "Progress is the only constant in training." Jack's programs focus on systematic strength building through intelligent progression.`,
        programs: [
            {
                id: 'progressive-foundation',
                name: 'Progressive Foundation',
                description: 'Systematic progression for bodyweight strength building',
                pattern: 'w@^~',
                reps: 8,
                sets: 4,
                focus: 'Strength Progression'
            },
            {
                id: 'leverage-master',
                name: 'Leverage Master',
                description: 'Using leverage manipulation for progressive overload',
                pattern: '@w^d_',
                reps: 6,
                sets: 5,
                focus: 'Leverage Training'
            },
            {
                id: 'tempo-control',
                name: 'Tempo Control',
                description: 'Time under tension protocols for strength building',
                pattern: 'w@d^',
                reps: 5,
                sets: 6,
                focus: 'Tempo Training'
            },
            {
                id: 'volume-progression',
                name: 'Volume Progression',
                description: 'Strategic volume increases for continuous growth',
                pattern: '@^wd~',
                reps: 10,
                sets: 4,
                focus: 'Volume Training'
            },
            {
                id: 'intensity-techniques',
                name: 'Intensity Techniques',
                description: 'Advanced techniques for breaking plateaus',
                pattern: 'w@^d_~',
                reps: 8,
                sets: 5,
                focus: 'Intensity Methods'
            },
            {
                id: 'strength-specialist',
                name: 'Strength Specialist',
                description: 'Elite strength development through progressive overload',
                pattern: '@w^~d_',
                reps: 4,
                sets: 7,
                focus: 'Maximum Strength'
            }
        ]
    },
    'mike-johnson': {
        id: 'mike-johnson',
        name: 'Mike Johnson',
        specialization: 'Beginner Development Coach',
        image: '/assets/images/v35.jpg',
        experience: '7 years',
        category: 'bodyweight',
        intro: `Mike Johnson is the coach every beginner wishes they had. A former personal trainer at major fitness chains, Mike discovered his calling in helping complete beginners build confidence and proper movement patterns. His approachable demeanor and patient teaching style make him perfect for those just starting their fitness journey. Mike specializes in breaking down complex movements into manageable steps and building sustainable habits. His philosophy: "Every expert was once a beginner." Mike's programs focus on building a solid foundation that sets students up for long-term success.`,
        programs: [
            {
                id: 'first-steps',
                name: 'First Steps',
                description: 'Gentle introduction to bodyweight training for complete beginners',
                pattern: '~wd',
                reps: 5,
                sets: 2,
                focus: 'Getting Started'
            },
            {
                id: 'confidence-builder',
                name: 'Confidence Builder',
                description: 'Building confidence through achievable progressions',
                pattern: 'w@d^',
                reps: 8,
                sets: 3,
                focus: 'Confidence Building'
            },
            {
                id: 'habit-former',
                name: 'Habit Former',
                description: 'Establishing consistent training habits and routines',
                pattern: '@wd^_',
                reps: 10,
                sets: 3,
                focus: 'Habit Formation'
            },
            {
                id: 'foundation-solid',
                name: 'Foundation Solid',
                description: 'Building a solid foundation for future progression',
                pattern: 'w@^d~',
                reps: 12,
                sets: 3,
                focus: 'Foundation Building'
            },
            {
                id: 'intermediate-bridge',
                name: 'Intermediate Bridge',
                description: 'Bridging the gap from beginner to intermediate',
                pattern: '@w^d_~',
                reps: 10,
                sets: 4,
                focus: 'Progression Bridge'
            },
            {
                id: 'confidence-complete',
                name: 'Confidence Complete',
                description: 'Complete confidence in bodyweight movement patterns',
                pattern: 'w@d^_~',
                reps: 12,
                sets: 4,
                focus: 'Complete Confidence'
            }
        ]
    }
};

export default bodyweightTrainers;